import { Button } from 'primereact/button';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import CoachingToDo from '../../../../components/CoachingToDo/CoachingToDo';
import DescriptionBox from '../../../../components/descriptionBox/DescriptionBox';
import { selectCurrentEmpid, selectCurrentUserFullName } from '../../../../pages/login/authSlice';
import { datetimeToDate, formatDate } from '../../../../utils/dateFormat';
import { ReplyCoachingStrength, usePutCoachingReplyCoachingStrengthMutation } from '../strengthSlice';
import { StrengthList } from '../types';

interface CommentCardStrengthProps {
    recieverId: string;
    receiverName: string;
    strengthId: number;
    isResponse?: boolean;
    listItem?: StrengthList;
    onCancel?: () => void;
    setIsResponse: (isResponse: boolean) => void;
}

const CommentCardStrength = ({
    recieverId,
    receiverName,
    strengthId,
    isResponse,
    listItem,
    onCancel,
    setIsResponse,
}: Readonly<CommentCardStrengthProps>) => {
    const [ReplyStrength] = usePutCoachingReplyCoachingStrengthMutation();
    const currEmpId = useSelector(selectCurrentEmpid);
    const [todoVisible, setTodoVisible] = useState(false);

    const currEmpName = useSelector(selectCurrentUserFullName);
    const [responseDescription, setResponseDescription] = useState<string | undefined>('');
    const now = new Date();
    const bottomRef = useRef<HTMLDivElement>(null);
    const topRef = useRef<HTMLDivElement>(null);
    const [allowEdit, setAllowEdit] = useState(false);

    const handleDescriptionChange = (value: string) => {
        setResponseDescription(value);
    };
    useEffect(() => {
        if (isResponse) {
            setResponseDescription(listItem?.response_description);
            window.scrollTo(0, 0);
        } else bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [isResponse]);

    const handleCancel = () => {
        setResponseDescription('');
        setAllowEdit(false);
        if (onCancel) {
            onCancel();
        }
    };

    const validate = () => {
        if (responseDescription && responseDescription.trim().length < 10) {
            toast.error('Description is required and must be at least 10 characters');
            return false;
        }

        if (responseDescription && responseDescription.trim().length > 600) {
            toast.error('Description cannot exceed 600 characters');
            return false;
        }
        return true;
    };

    const handleEdit = () => {
        if (listItem?.response_employee_number === currEmpId) {
            setAllowEdit(true);
        } else {
            toast.warning('Only author can edit.');
        }
    };

    const handleSave = () => {
        if (!validate()) return;
        const request: ReplyCoachingStrength = {
            coachingStrengthId: listItem?.coaching_strength_id,
            responseEmployeeNumber: currEmpId,
            responseDescription,
        };
        ReplyStrength({ replyCoachingStrength: request })
            .unwrap()
            .then((res: any) => {
                if (res?.data) {
                    toast.success('Strength updated successfully');
                    if (setIsResponse) {
                        setIsResponse(true);
                        setAllowEdit(false);
                    }
                } else {
                    toast.error('Error updating Discussion');
                }

                if (onCancel) {
                    onCancel();
                }
            })
            .catch(() => {
                toast.error('Error updating Strength');
            });
    };

    return (
        <div className="flex-coaching" ref={topRef}>
            <div className="col-1 p-3">
                <i className="turnDownRight" />
            </div>

            <div className="logConversation-coaching col-11 mb-4">
                <div className="d-flex justify-content-start mb-2">
                    <div className="col-3 mb-3">
                        <h6>Date</h6>
                        <div>
                            {isResponse && listItem?.response_date
                                ? formatDate(datetimeToDate(listItem?.response_date))
                                : formatDate(datetimeToDate(now))}
                        </div>
                    </div>
                    {isResponse && (
                        <Button className="editBtnIcon p-button-outlined border-0" onClick={handleEdit} />
                    )}
                </div>
                <div className="mb-4">
                    <DescriptionBox
                        createdByLabel="Response by"
                        createdByValue={isResponse ? listItem?.response_employee_name : currEmpName}
                        title={'Description'}
                        limit={600}
                        readOnly={!allowEdit && isResponse}
                        responseDescription={
                            isResponse ? listItem?.response_description : responseDescription
                        }
                        value={allowEdit ? listItem?.response_description : responseDescription}
                        onChange={handleDescriptionChange}
                    />
                    <div ref={bottomRef} />
                </div>

                <div className="flexButtons mt-3">
                    {!isResponse || allowEdit ? (
                        <>
                            <Button
                                label="Cancel"
                                onClick={handleCancel}
                                className="btn-secondary me-2"
                            />
                            <Button
                                label="Save"
                                onClick={handleSave}
                                className="btn-primary me-2"
                            />
                        </>
                    ) : (
                        ''
                    )}
                    {isResponse && (
                        <CoachingToDo
                            receiverId={recieverId}
                            receiverName={receiverName}
                            visible={todoVisible}
                            setVisible={setTodoVisible}
                            coachingEntityTypeId={3}
                            coachingEntityId={strengthId}
                            coachingStatusId={1}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
export default CommentCardStrength;
